import React from 'react';
import TopNav from '../components/TopNav';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-neutral-950">
      <TopNav />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-neutral-900 rounded-lg p-8">
            <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
            <p className="text-neutral-400 mb-8">The Traders • Last updated: January 25, 2026</p>

            <div className="prose prose-invert max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">1. Overview</h2>
                <p className="text-neutral-300">
                  This Privacy Policy explains how The Traders collects, uses, shares, and protects information when you use our Services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>

                <h3 className="text-xl font-medium text-white mb-3">Information You Provide</h3>
                <ul className="list-disc list-inside text-neutral-300 space-y-2 mb-4">
                  <li>Account details such as name, email, phone number, and password.</li>
                  <li>Support requests, feedback, and communications.</li>
                  <li>Billing details processed by third-party payment providers.</li>
                </ul>

                <h3 className="text-xl font-medium text-white mb-3">Information Collected Automatically</h3>
                <ul className="list-disc list-inside text-neutral-300 space-y-2 mb-4">
                  <li>Device and usage data such as IP address, browser type, OS, and pages visited.</li>
                  <li>Log data, diagnostics, and error reports.</li>
                  <li>Cookies and similar technologies for preferences, analytics, and security.</li>
                </ul>

                <h3 className="text-xl font-medium text-white mb-3">Trading and Service Data</h3>
                <ul className="list-disc list-inside text-neutral-300 space-y-2 mb-4">
                  <li>Trading activity, strategy settings, and alerts.</li>
                  <li>Market data and interaction data used for analytics and model improvement.</li>
                </ul>

                <h3 className="text-xl font-medium text-white mb-3">Information From Third Parties</h3>
                <ul className="list-disc list-inside text-neutral-300 space-y-2">
                  <li>Broker or exchange information you authorize us to access.</li>
                  <li>Identity or compliance data where required by law.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Information</h2>
                <ul className="list-disc list-inside text-neutral-300 space-y-2">
                  <li>Provide, maintain, and improve the Services.</li>
                  <li>Operate features like alerts, analytics, and AI-driven insights.</li>
                  <li>Monitor performance, prevent abuse, and enhance security.</li>
                  <li>Comply with legal, regulatory, and contractual obligations.</li>
                  <li>Communicate updates, security notices, and support responses.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">4. Legal Bases for Processing (GDPR)</h2>
                <p className="text-neutral-300 mb-4">
                  Where applicable, we process information based on contract necessity, legitimate interests, legal obligations,
                  and your consent for optional features.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">5. How We Share Information</h2>
                <ul className="list-disc list-inside text-neutral-300 space-y-2">
                  <li>Service providers for hosting, analytics, support, and security.</li>
                  <li>Broker, exchange, or data partners you choose to connect.</li>
                  <li>Payment processors for subscription and billing operations.</li>
                  <li>Legal or regulatory authorities when required.</li>
                  <li>Business transfers such as mergers or acquisitions.</li>
                  <li>Aggregated or de-identified data that cannot reasonably identify you.</li>
                </ul>
                <p className="text-neutral-300 mt-4">We do not sell your personal information.</p>
                <p className="text-neutral-300 mt-4">
                  <strong>No Affiliation</strong>: The Traders is not affiliated with, endorsed by, or sponsored by Alpaca Markets, Inc. ("Alpaca") or any other broker-dealer, exchange, or financial institution. We are an independent service provider.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">6. AI and Analytics</h2>
                <p className="text-neutral-300">
                  We may use de-identified or aggregated data to improve models and analytics. We do not use personal
                  information to train models in a way that allows others to identify you.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">7. Data Retention</h2>
                <p className="text-neutral-300">
                  We retain personal information only as long as necessary to provide the Services, meet legal obligations,
                  resolve disputes, and enforce agreements. We delete or de-identify information when it is no longer needed.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">8. Security</h2>
                <p className="text-neutral-300">
                  We use reasonable technical and organizational measures to protect information, including encryption,
                  access controls, and monitoring. No system is completely secure.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">9. International Transfers</h2>
                <p className="text-neutral-300">
                  Your information may be processed in countries other than your own. We use appropriate safeguards for
                  international transfers where required.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">10. Your Rights and Choices</h2>
                <ul className="list-disc list-inside text-neutral-300 space-y-2">
                  <li>Access, correct, or delete your personal information.</li>
                  <li>Object to or restrict certain processing.</li>
                  <li>Request data portability.</li>
                  <li>Withdraw consent where processing is based on consent.</li>
                  <li>File a complaint with your local data protection authority.</li>
                </ul>
                <p className="text-neutral-300 mt-4">You can opt out of marketing communications at any time.</p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">11. Children&apos;s Privacy</h2>
                <p className="text-neutral-300">
                  Our Services are not directed to individuals under 18, and we do not knowingly collect personal
                  information from minors.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to This Policy</h2>
                <p className="text-neutral-300">
                  We may update this Privacy Policy from time to time. If changes are material, we will notify you through
                  the Services or by email.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-white mb-4">13. Contact Us</h2>
                <p className="text-neutral-300 mb-2">For privacy requests or questions, contact us through:</p>
                <ul className="list-disc list-inside text-neutral-300 space-y-2">
                  <li>The in-app support system, or</li>
                  <li>privacy@thetraders.ai.</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;